// src/utils/accountRole.js
//
// ONE answer to "what kind of account is this person?" — used by the login
// redirect, both dashboards, the profile badge, and the subject links.
//
// WHY THIS FILE EXISTS
// --------------------
// Registration writes `role: "unpaid"` and stores what the person actually
// signed up as in `intendedRole`. Only the Stripe webhook ever promotes
// `role` to something like "va-3rd-educator".
//
// Every piece of routing in the app asked `role.includes("educator")`. For an
// unpaid or trialling account that is false, so an educator who had not yet
// been promoted was treated as a student: sent to /dashboard, shown the
// student subject pages, and given no Educator Tools tile. That is the "I
// signed up as an educator but it shows me the student pages" bug, and it is
// not a payment bug — it is the app reading the wrong field.
//
// The rule here: a granted role wins if there is one, otherwise fall back to
// the intent captured at signup. Access is NOT decided here — useAccessLevel
// and PrivateRoute still do that. This only decides which *shape* of the
// product someone sees, which is safe to answer from intent.

import { SIGNUP_ROLES, UNPAID_ROLE } from "../config/plans";

export const ROLE_KIND = {
  ADMIN: "admin",
  EDUCATOR: "educator",
  STUDENT: "student",
  PARENT: "parent",
  DISTRICT: "district",
  UNKNOWN: "unknown",
};

/**
 * True when `role` is a real role handed out by the webhook or claimSeat,
 * e.g. "va-3rd-educator". False for "unpaid", "", null.
 *
 * A granted role is also proof that money moved: nothing but the webhook and
 * claimSeat can write this field.
 */
export function isGrantedRole(role) {
  const r = String(role || "").trim().toLowerCase();
  if (!r) return false;
  if (r === UNPAID_ROLE) return false;
  if (r === "pending") return false;
  return (
    r.includes("educator") ||
    r.includes("student") ||
    r.includes("admin") ||
    r === "silver"
  );
}

/**
 * What kind of account this is, for display and navigation.
 *
 * @param {object} profile users/{uid} document
 * @param {object} opts    { isAdmin } — the admin custom claim, if known
 */
export function effectiveRoleKind(profile = {}, opts = {}) {
  if (opts.isAdmin) return ROLE_KIND.ADMIN;

  const granted = String(profile?.role || "").toLowerCase();

  if (granted.includes("administrator") || granted === "admin") {
    return ROLE_KIND.ADMIN;
  }

  // A granted role is the most reliable signal we have.
  if (isGrantedRole(granted)) {
    if (granted.includes("educator")) return ROLE_KIND.EDUCATOR;
    if (granted.includes("student")) return ROLE_KIND.STUDENT;
  }

  // Not promoted yet — use what they told us at signup.
  const intended = String(profile?.intendedRole || "").toLowerCase();
  if (intended === SIGNUP_ROLES.EDUCATOR) return ROLE_KIND.EDUCATOR;
  if (intended === SIGNUP_ROLES.STUDENT) return ROLE_KIND.STUDENT;
  if (intended === SIGNUP_ROLES.PARENT) return ROLE_KIND.PARENT;
  if (intended === SIGNUP_ROLES.DISTRICT) return ROLE_KIND.DISTRICT;

  // Someone holding a seat out of an educator's purchase is a student.
  if (profile?.seatSourceOf) return ROLE_KIND.STUDENT;

  return ROLE_KIND.UNKNOWN;
}

/**
 * Which dashboard this person belongs on.
 *
 * A parent gets the student-style dashboard: they're managing a child's
 * learning space, not running a classroom. Change this one line if you decide
 * parents should get educator tooling — nothing else needs to know.
 */
export function dashboardPathFor(profile = {}, opts = {}) {
  switch (effectiveRoleKind(profile, opts)) {
    case ROLE_KIND.ADMIN:
      return "/admin";
    case ROLE_KIND.EDUCATOR:
      return "/educator/dashboard";
    case ROLE_KIND.DISTRICT:
      return "/educator/dashboard";
    default:
      return "/dashboard";
  }
}

/**
 * The `:view` segment of /portal/:state/:grade/:subject/:view.
 *
 * Only two portal views exist. Parents and district buyers see the educator
 * view because it carries the teaching notes and the student roster, which is
 * what someone supervising a learner wants.
 */
export function portalViewFor(profile = {}, opts = {}) {
  const kind = effectiveRoleKind(profile, opts);
  if (
    kind === ROLE_KIND.EDUCATOR ||
    kind === ROLE_KIND.DISTRICT ||
    kind === ROLE_KIND.ADMIN
  ) {
    return "educator";
  }
  return "student";
}

const LABELS = {
  [ROLE_KIND.ADMIN]: { icon: "🛠️", label: "Administrator" },
  [ROLE_KIND.EDUCATOR]: { icon: "🧑‍🏫", label: "Educator" },
  [ROLE_KIND.STUDENT]: { icon: "🎒", label: "Student" },
  [ROLE_KIND.PARENT]: { icon: "👪", label: "Parent" },
  [ROLE_KIND.DISTRICT]: { icon: "🏫", label: "School / District" },
  [ROLE_KIND.UNKNOWN]: { icon: "👤", label: "Member" },
};

/**
 * { icon, label } for the account type — "🧑‍🏫 Educator".
 */
export function roleDisplay(profile = {}, opts = {}) {
  return LABELS[effectiveRoleKind(profile, opts)] || LABELS[ROLE_KIND.UNKNOWN];
}

/**
 * "Virginia · Grade 3" style context line, skipping whatever is missing.
 */
export function scopeLabel(profile = {}) {
  const parts = [];
  if (profile?.state) parts.push(String(profile.state).toUpperCase());
  if (profile?.grade) parts.push(`Grade ${profile.grade}`);
  return parts.join(" · ");
}

export default effectiveRoleKind;
